import { KikRestVerbs } from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { OrdersDtoOrderDto } from "@springtree/eva-services-core";
import { useRouter } from "next/router";
import { useFormatter, useNow, useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import Button from "~/components/common/button";
import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { ApiConstants } from "~/shared/api/api-constants.class";
import appRoutes from "~/utils/app-routes";

import styles from "./cancel-order.module.scss";
import CancelOrderDialog from "./cancel-order-dialog";

type Props = {
  order: OrdersDtoOrderDto;
};

export default function GuestCancelOrder({ order }: Props) {
  const t = useTranslations();
  const router = useRouter();
  const formatter = useFormatter();
  const { guestTrackingToken } = useAuthContext();
  const { configuration } = useConfiguration();
  const openCancel = router.query.openCancel == "true";
  const [openCancelModal, setOpenCancelModal] = useState(openCancel ?? false);
  const [successfullyCancelled, setSuccessfullyCancelled] = useState(false);
  const [pendingCancellation, setPendingCancellation] = useState(false);

  const exportInMinutes = configuration["Orders:DelayMinutes"] ?? 10;

  const now = useNow({
    updateInterval: 1000,
  });

  const cancelableUntil = useMemo(() => {
    if (order?.PlacementDate) {
      const maxCancelDate = new Date(order.PlacementDate);
      maxCancelDate.setMinutes(maxCancelDate.getMinutes() + exportInMinutes);
      return maxCancelDate;
    }
    return new Date();
  }, [order, exportInMinutes]);

  const canBeCancelled = useMemo(() => {
    const isSdd = order.Lines?.some(
      (line) => !line.IsCancelled && line.ProductID && line.ShippingMethod?.DeliveryType === 2
    );
    const notCancelledProducts = order.Lines?.filter((line) => line.ProductID && !line.IsCancelled);

    return (
      !isSdd &&
      notCancelledProducts &&
      notCancelledProducts.length > 0 &&
      notCancelledProducts.every((line) => line.ExportStatusID! <= 2) &&
      cancelableUntil &&
      now < cancelableUntil
    );
  }, [order.Lines, cancelableUntil, now]);

  if (!canBeCancelled) {
    return null;
  }

  return (
    <>
      <div className={styles.cancelBanner}>
        <p>
          {t("generic.cancelorderuntil", {
            time: formatter.relativeTime(cancelableUntil, now),
          })}
        </p>
        <Button lookAsLink onClick={() => setOpenCancelModal(true)}>
          {t("generic.cancelorder")}
        </Button>
      </div>
      <CancelOrderDialog
        isOpen={openCancelModal}
        isPaid={order.IsPaid}
        onClose={() => {
          if (successfullyCancelled) {
            router.push(appRoutes.HOMEPAGE);
          } else {
            setOpenCancelModal(false);
          }
        }}
        onSubmit={async () => {
          setPendingCancellation(true);
          try {
            const cancelResponse = await HttpService.$http({
              headers: {
                Authorization: `Bearer ${guestTrackingToken}`,
              },
              url: ApiConstants.endpointsMapiV1.ecoGuestTrackOrderCancel,
              method: KikRestVerbs.DELETE,
            });
            Logger.instance.info("guest cancelResponse: ", cancelResponse);
            if (cancelResponse) {
              setSuccessfullyCancelled(true);
            }
          } catch (error) {
            Logger.instance.error("guest cancelResponse: ", error);
          } finally {
            setPendingCancellation(false);
          }
        }}
        isLoading={pendingCancellation}
        successfullyCancelled={successfullyCancelled}
        timeoutCancel={!canBeCancelled}
      />
    </>
  );
}
